import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BodyText, Button, ColorDot, Div, Dropdown, Flex, Icon, PricingText } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';

import './ApprovalChain.css';
import { showGrowlNotification } from '../../../../store/actions';
import { ApprovalChainTemplateOption, UserGroup } from '../../../../types';
import { ApproverList } from '@cdaas/api-clients/dist/asset-user-openapi';

type ApprovalChainProps = {
  approvalChain: ApproverList;
  selectedUserGroup: UserGroup;
  allApprovers: ApproverList;
  handleAddNewApproval: (newApproval: ApprovalChainTemplateOption) => void;
  handleRemoveApproval: (index: number) => void;
  handleApprovalOrderChange: (index: number, move: 'up' | 'down') => void;
};

const ApprovalChain: FunctionComponent<ApprovalChainProps> = ({
  approvalChain,
  selectedUserGroup,
  allApprovers,
  handleRemoveApproval,
  handleApprovalOrderChange,
  handleAddNewApproval,
}) => {
  const [newApprovalValue, setNewApprovalValue] = useState<ApprovalChainTemplateOption>({ label: '', value: '' });
  const [newApprovalMode, setNewApprovalMode] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation('onboarding');

  useEffect(() => {
    setNewApprovalMode(false);
  }, [selectedUserGroup]);

  const handleNewApprovalMode = () => {
    if (approvalChain.contents.length > 3) {
      dispatch(showGrowlNotification(t('groupSettingsPage.maxApprovalChainNotificationText'), 'warning'));
    } else {
      setNewApprovalMode(true);
    }
  };

  const initDropdownOptions = (): ApprovalChainTemplateOption[] => {
    const approvers = allApprovers.contents.map(approver => ({ label: approver.name, value: approver.id }));
    return [...approvers];
  };

  const getClassForCssLine = (index: number) => {
    if (index !== approvalChain.contents.length - 1) return 'approval-chain-border-solid';
    else return 'approval-chain-border-dashed';
  };

  return (
    <>
      {approvalChain.contents.map((approver, i) => {
        return (
          <Fragment key={i}>
            <Flex margin={i === 0 ? { bottom: 'sm' } : undefined}>
              <Div className={'approval-chain-color-dot'} margin={{ right: 'xlg', top: 'xxs' }}>
                <ColorDot color={'#990AE3'} />
              </Div>

              {/* Title and name of approver (user group or user) */}
              <Flex directionColumn margin={{ right: 'xxlg' }}>
                <PricingText size={'sm'} color={'purple500'}>
                  {i === 0 ? t('groupSettingsPage.purchaseOrder') : t('groupSettingsPage.approval')}
                </PricingText>
                <BodyText size={'xs'}>{approver.name}</BodyText>
              </Flex>

              {/* Order and remove icons */}
              {i !== 0 && (
                <Div className={'approval-chain-icons-order-and-remove'}>
                  <Icon clickableAppearance name={'arrow-up'} size="md" onClick={() => handleApprovalOrderChange(i, 'up')} />
                  <Icon
                    clickableAppearance
                    name={'arrow-down'}
                    size="md"
                    margin={{ left: 'xxs', right: 'md' }}
                    onClick={() => handleApprovalOrderChange(i, 'down')}
                  />
                  <Icon clickableAppearance name={'stop'} size="md" onClick={() => handleRemoveApproval(i)} />
                </Div>
              )}
            </Flex>

            {/* Custom-made line border between color dots */}
            <Div className={getClassForCssLine(i)}></Div>
          </Fragment>
        );
      })}

      <Flex className={'approval-chain-add-new-approval-flex'}>
        {/* Add new approval icons */}
        <Icon clickableAppearance color={'purple500'} margin={{ right: 'lg' }} size={'md'} name={'add'} onClick={handleNewApprovalMode}></Icon>
        {!newApprovalMode && (
          <BodyText size={'xs'} clickableAppearance margin={{ top: 'xxxs' }} color={'purple500'} onClick={handleNewApprovalMode}>
            {`+ ${t('groupSettingsPage.approvalChain.addNewButton')}`}
          </BodyText>
        )}
      </Flex>

      {/* Add new approval dropdown with buttons */}
      {newApprovalMode && (
        <Div margin={{ left: 'xlg' }}>
          <Dropdown
            className={'approval-chain-new-approval-chain-selection'}
            label={t('groupSettingsPage.approvalChain.groupOrUserTitle')}
            onChange={(_e, option) => {
              setNewApprovalValue({ label: option.label, value: option.value });
            }}
            selectedOption={{
              label: newApprovalValue.label,
              value: newApprovalValue.value,
            }}
            options={initDropdownOptions()}
          />
          <Button
            margin={{ top: 'sm', right: 'xlg' }}
            onClick={() => {
              handleAddNewApproval(newApprovalValue);
              setNewApprovalMode(false);
            }}
          >
            {t('groupSettingsPage.approvalChain.addLevelButton')}
          </Button>
          <Button variant={'text'} onClick={() => setNewApprovalMode(false)}>
            {t('groupSettingsPage.approvalChain.cancelButton')}
          </Button>
        </Div>
      )}
    </>
  );
};

export default ApprovalChain;
